"use client";

import { useEffect, useState } from "react";
import {
  Avatar,
  Embed,
  I,
  Message,
  Slash,
  SlashPopover,
} from "@/components/kamibot-v2/discord";

type TypewriterOptions = {
  typeSpeed?: number;
  holdMs?: number;
  eraseSpeed?: number;
  loop?: boolean;
};

function useTypewriter(steps: string[], opts: TypewriterOptions = {}) {
  const { typeSpeed = 60, holdMs = 1600, eraseSpeed = 30, loop = true } = opts;
  const [text, setText] = useState("");
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const target = steps[stepIdx];

    async function run() {
      // type
      for (let i = 0; i <= target.length; i++) {
        if (cancelled) return;
        setText(target.slice(0, i));
        await new Promise<void>((r) => {
          timer = setTimeout(r, typeSpeed);
        });
      }
      // hold
      await new Promise<void>((r) => {
        timer = setTimeout(r, holdMs);
      });
      // erase
      for (let i = target.length; i >= 0; i--) {
        if (cancelled) return;
        setText(target.slice(0, i));
        await new Promise<void>((r) => {
          timer = setTimeout(r, eraseSpeed);
        });
      }
      if (!cancelled) {
        setStepIdx((p) =>
          loop ? (p + 1) % steps.length : Math.min(p + 1, steps.length - 1)
        );
      }
    }
    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);

  return { text, stepIdx };
}

export function HeroChat() {
  const slashSteps = [
    "/tts 안녕하세요",
    "/번역 こんにちは",
    "@카미봇 회의 요약",
    "/이모지 추가",
  ];
  const { text } = useTypewriter(slashSteps, {
    typeSpeed: 70,
    holdMs: 1800,
    eraseSpeed: 28,
  });
  const [showPop, setShowPop] = useState(true);

  // Hide popover briefly between cycles
  useEffect(() => {
    setShowPop(text.startsWith("/"));
  }, [text]);

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <span className="hash">#</span>
        <span className="ch-name">우리-서버-잡담</span>
        <span className="ch-topic">카미봇이 항상 깨어있어요</span>
        <span className="ch-spacer" />
        <span className="ch-pill">
          <span className="dot" />온라인 14,287
        </span>
      </div>

      <div className="chat-body">
        <Message
          avatar={
            <div className="av user">
              <img
                src="/avatars/kamilake.webp"
                alt="Kamilake"
                style={{ objectPosition: "center center" }}
              />
            </div>
          }
          name="Kamilake"
          time="오후 3:14"
        >
          오 디코서버 만들었는데 봇 뭐쓰지
        </Message>

        <Message
          avatar={
            <div className="av user2">
              <img
                src="/avatars/mochi_dev.webp"
                alt="mochi_dev"
                style={{ objectPosition: "center center" }}
              />
            </div>
          }
          name="mochi_dev"
          time="오후 3:14"
        >
          <Slash name="초대" /> 카미봇 한번 써봐 한국어 잘 알아들어
        </Message>

        <Message avatar={<Avatar />} isBot verified name="카미봇" time="오후 3:15">
          안녕하세요! 무엇을 도와드릴까요?{" "}
          <span style={{ opacity: 0.6 }}></span>
          <Embed
            title="✦ 카미봇이 도착했어요"
            desc="복잡한 게 싫어서 디스코드 서버를 편하게 쓰려고 만든 봇이에요. 한번 써보고 아쉬운 점은 언제든 알려주세요."
            fields={[
              { label: "준비된 기능", value: "TTS · AI · 번역 · 이모지 · 환영 · 음악" },
              { label: "지원 언어", value: "한국어 중심 · 108개 언어" },
              { label: "비용", value: "핵심 기능 무료" },
              { label: "지원 서버", value: "/도와줘 로 언제든지" },
            ]}
          />
        </Message>

        <Message
          avatar={
            <div className="av user">
              <img
                src="/avatars/kamilake.webp"
                alt="Kamilake"
                style={{ objectPosition: "center center" }}
              />
            </div>
          }
          name="Kamilake"
          time="오후 3:15"
        >
          오 귀엽다 일단 초대
        </Message>
      </div>

      <div className="typing">
        <b>카미봇</b>
        <span>이 입력 중</span>
        <span className="dots">
          <span />
          <span />
          <span />
        </span>
      </div>

      <div className="chat-input">
        <I.plus className="icon" />
        <div className="field">
          <span style={{ color: "var(--kbv2-primary-strong)" }}>
            {text || "메시지 보내기 #우리-서버-잡담"}
          </span>
          <span className="caret" />
          {showPop && <SlashPopover active={Math.min(3, text.length % 5)} />}
        </div>
        <I.emoji className="icon" />
        <I.gift className="icon" />
      </div>
    </div>
  );
}
